// S9 AI (REQ-7.3): auto-accept per environment. An auto-accepted bug goes to its best-matching Editor,
// In progress, or waits in Ready when nobody matches (poc CT-007). Every Editor and Admin sees and scores
// the AI reports, so there's no per-app setting for them (poc CT-008). An app that hands its tickets to Linear
// keeps an auto-assigned bug in Ready / Assigned until Linear says the work has started (poc CT-009).

import type { SettingCode } from '@feedback/shared/constants/settings';
import type { AppView } from '@feedback/shared/contracts/apps';
import type { SettingView } from '@feedback/shared/contracts/settings';
import { useQueries } from '@tanstack/react-query';
import { errorText } from '../../lib/admin-api';
import { SettingControl } from '../../settings/setting-control';
import { Panel } from '../../ui/primitives';
import { appSettingsQuery, useSettingSaver } from '../app-data';

function findSetting(settings: SettingView[] | undefined, code: SettingCode): SettingView | undefined {
  return settings?.find((s) => s.code === code);
}

/** One environment-level boolean for every environment of the app. */
function PerEnvironmentSwitches({ app, code, title }: { app: AppView; code: SettingCode; title: string }) {
  const saver = useSettingSaver(app.id);
  const results = useQueries({ queries: app.environments.map((env) => appSettingsQuery(app.id, env.id)) });
  return (
    <div>
      <h3 className="text-sm font-semibold">{title}</h3>
      <ul className="mt-2 flex flex-col gap-2">
        {app.environments.map((env, i) => {
          const setting = findSetting(results[i]?.data, code);
          return (
            <li key={env.id} className="flex items-center justify-between gap-3 text-sm sm:justify-start">
              <span className="sm:w-40">{env.name}</span>
              {setting ? (
                <SettingControl
                  setting={setting}
                  saver={saver}
                  label={`${title}: ${env.name}`}
                  disabled={!app.can.manageSettings}
                />
              ) : results[i]?.isError ? (
                <span className="text-danger">{errorText(results[i]?.error)}</span>
              ) : (
                <span className="text-muted">Loading…</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function AiSection({ app }: { app: AppView }) {
  const handsOffToLinear = app.linearConnected && app.linear !== null;
  return (
    <Panel
      title="AI"
      description={
        handsOffToLinear
          ? 'An auto-accepted bug is assigned to its best-matching Editor and handed to Linear; it waits in Ready / Assigned until the work starts in Linear. If nobody matches, it waits in Ready.'
          : 'An auto-accepted bug is assigned to its best-matching Editor and goes straight to In progress. If nobody matches, it waits in Ready.'
      }
    >
      <PerEnvironmentSwitches app={app} code="app.auto_accept" title="Auto-accept low-risk bugs" />
    </Panel>
  );
}
